import React from 'react'

/**
 * A custom React hook for using Local Storage.
 *
 * @template T
 * @param {string} key - The key to use for local storage.
 * @param {T} initialValue - The initial value to store.
 * @returns {[T, (value: T) => void]} An array containing the stored value and a setter function.
 *
 * @example
 * const [value, setValue] = useLocalStorage('key', 'defaultValue');
 */
function useLocalStorage(key, initialValue) {
  const [storedValue, setStoredValue] = React.useState(() => {
    try {
      const item = window.localStorage.getItem(key)
      return item ? JSON.parse(item) : initialValue
    } catch (error) {
      console.warn(error)
      return initialValue
    }
  })
  const setValue = React.useCallback(
    value => {
      try {
        const valueToStore = value instanceof Function ? value(storedValue) : value
        setStoredValue(valueToStore)
        window.localStorage.setItem(key, JSON.stringify(valueToStore))
      } catch (error) {
        console.warn(error)
      }
    },
    [storedValue, key]
  )

  React.useEffect(() => {
    try {
      const item = window.localStorage.getItem(key)
      setStoredValue(item ? JSON.parse(item) : initialValue)
    } catch (error) {
      console.warn(error)
      setStoredValue(initialValue)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])

  return React.useMemo(() => [storedValue, setValue], [storedValue, setValue])
}

export default useLocalStorage
